import React, { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import WonderBeautiesConsultation from './WonderBeautiesConsultation'
import FormFollowUp from './components/FormFollowUp'
import AdminLogin from './components/admin/AdminLogin'
import AdminDashboard from './components/admin/AdminDashboard'
import UserManagement from './components/admin/UserManagement'
import PharmacistManagement from './components/admin/PharmacistManagement'
import PendingConsultationsView from './components/admin/PendingConsultationsView'
import DailyConsultationReports from './components/admin/DailyConsultationReports'
import PharmacistAssignmentLogs from './components/admin/PharmacistAssignmentLogs'
import MFASetup from './components/admin/MFASetup'
import SecureAdminRoute from './components/admin/SecureAdminRoute'
import { supabase } from './lib/supabase'
import './App.css'

// 🔒 DEPRECATED: Old AdminWrapper - replaced by SecureAdminRoute
// This old wrapper did NOT enforce MFA properly, allowing URL bypass attacks
// const AdminWrapper = () => { ... }

// User Management wrapper - requires super_admin role
// 🔒 Legacy wrappers removed in favor of unified SecureAdminRoute protection

// Pharmacist Management wrapper - requires super_admin or coordinator role
// 🔒 All management wrappers consolidated into SecureAdminRoute

// 🔒 MFA Setup Wrapper - ensures user is logged in before showing MFA setup
// Also redirects to dashboard if MFA is already verified
const MFASetupWrapper = () => {
  const location = useLocation()
  const [loading, setLoading] = useState(true)
  const [shouldShowSetup, setShouldShowSetup] = useState(false)
  const isMfaResetFlow = location.state?.mfaReset === true

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if user has a valid session
        const { data: { session } } = await supabase.auth.getSession()

        if (!session) {
          console.log('❌ No session for MFA setup, redirecting to login')
          setShouldShowSetup(false)
          setLoading(false)
          return
        }

        // 🔥 MFA RESET CHECK: If reset was just requested, force setup
        // This handles cases where unenrollment might have latency
        if (isMfaResetFlow) {
          setShouldShowSetup(true)
          setLoading(false)
          return
        }

        // Check if MFA is already verified
        const { data: factors } = await supabase.auth.mfa.listFactors()
        const verifiedFactors = [
          ...(factors?.totp || []),
          ...(factors?.all || [])
        ].filter(f => f.verified === true || f.status === 'verified')

        if (verifiedFactors.length > 0) {
          console.log('✅ MFA already verified, redirecting to dashboard')
          setShouldShowSetup(false)
          setLoading(false)
          return
        }

        // User is logged in but MFA not verified - show setup
        console.log('✅ User logged in, MFA not verified - showing setup')
        setShouldShowSetup(true)
        setLoading(false)

      } catch (error) {
        console.error('❌ MFA setup wrapper error:', error)
        setShouldShowSetup(false)
        setLoading(false)
      }
    }

    checkAuth()
  }, [isMfaResetFlow])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#A73A5D]"></div>
      </div>
    )
  }

  if (!shouldShowSetup) {
    return <Navigate to="/admin" replace />
  }

  return <MFASetup />
}

function App() {
  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Main consultation route */}
          <Route path="/" element={<WonderBeautiesConsultation />} />

          {/* Google Sheet form follow-up route */}
          <Route path="/from_form" element={<FormFollowUp />} />

          {/* Admin routes - Unified MFA Protection */}
          <Route path="/admin" element={<SecureAdminRoute><AdminDashboard /></SecureAdminRoute>} />
          <Route path="/admin/dashboard" element={<Navigate to="/admin" replace />} />
          <Route path="/admin/users" element={<SecureAdminRoute requiredRole="super_admin"><UserManagement /></SecureAdminRoute>} />
          <Route path="/admin/pharmacists" element={<SecureAdminRoute requiredRole="coordinator"><PharmacistManagement /></SecureAdminRoute>} />
          <Route path="/admin/pending" element={<SecureAdminRoute requiredRole="coordinator"><PendingConsultationsView /></SecureAdminRoute>} />
          <Route path="/admin/reports" element={<SecureAdminRoute requiredRole="coordinator"><DailyConsultationReports /></SecureAdminRoute>} />
          <Route path="/admin/assignment-logs" element={<SecureAdminRoute requiredRole="coordinator"><PharmacistAssignmentLogs /></SecureAdminRoute>} />
          <Route path="/admin/mfa-setup" element={<MFASetupWrapper />} />
          <Route path="/admin/login" element={<AdminLogin onLoginSuccess={() => { }} />} />

          {/* Catch-all redirect to main app */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
  )
}

export default App
